
import { plotDataTableConfigs } from './plotDataTableConfigs';
import { therapistTableConfigs } from './therapistTableConfigs';
import { yearEndRxUnfinishedValuesTableConfigs } from './yearEndRxUnfinishedValuesTableConfigs';

interface DownloadConfig {
  data: any[];
  columns: any[];
  title: string;
}

export const patientStatsDownloadConfig = (
  plot: string,
  table: string,
  dataMap: Record<string, any[]>
): DownloadConfig | null => {
  if (table === "Zusammenfassung") return null;

  switch (plot) {
    case "cumulative patients":
      return {
        data: dataMap.cumulativePatients,
        columns: plotDataTableConfigs.cumulativePatients.columns,
        title: "Kumulative Patienten"
      };
    case "new patients":
      return {
        data: dataMap.newPatients,
        columns: plotDataTableConfigs.newPatients.columns,
        title: "Neue Patienten"
      };
    default:
      throw new Error(`Unsupported patient plot type: ${plot}`);
  }
};

export const rxStatsDownloadConfig = (
  plot: string,
  table: string,
  dataMap: Record<string, any[]>
): DownloadConfig | null => {
  if (table === "Zusammenfassung") return null;

  switch (plot) {
    case "all rx count":
      return {
        data: dataMap.allRxCount,
        columns: plotDataTableConfigs.totalRxCount.columns,
        title: "Anzahl der Verordnungen"
      };
    case "all rx value":
      return {
        data: dataMap.allRxValue,
        columns: plotDataTableConfigs.totalRxValue.columns,
        title: "Gesamtwert der Verordnungen"
      };
    case "rx therapist count":
      return {
        data: dataMap.rxTherapistCountOriginal, // Use original data without JSX elements
        columns: plotDataTableConfigs.rxTherapistCount.downloadColumns,
        title: "Anzahl der Therapeuten pro Rezept"
      };
    case "rx lifespan distribution":
      return {
        data: dataMap.rxLifespanOriginal, // Use original data without JSX elements
        columns: plotDataTableConfigs.rxLifespan.downloadColumns,
        title: "Verteilung der Bearbeitungs dauer der Rezepten"
      }
    default:
      throw new Error(`Unsupported rx plot type: ${plot}`);
  }
};

export const scheduleStatsDownloadConfig = (
  plot: string,
  table: string,
  dataMap: Record<string, any[]>
): DownloadConfig | null => {
  if (table === "Zusammenfassung") return null;

  switch (plot) {
    case "total appointment value":
      return {
        data: dataMap.totalApptValue,
        columns: plotDataTableConfigs.totalApptValue.columns,
        title: "Gesamtwert der Termine"
      };
    case "all rx count":
      return {
        data: dataMap.totalScheduleBasedRxCount,
        columns: plotDataTableConfigs.totalRxCount.columns,
        title: "Anzahl der Verordnungen"
      };
    case "total gaps":
      return {
        data: dataMap.totalScheduleBasedGaps,
        columns: plotDataTableConfigs.totalGaps.columns,
        title: "Gesamte Lücken"
      };
    case "therapyRx frequency":
      return {
        data: dataMap.therapyRxFrequency,
        columns: plotDataTableConfigs.therapyRxFrequency.columns,
        title: "Behandlung Frequenzverteilung",
      };
    default:
      throw new Error(`Unsupported schedule plot type: ${plot}`);
  }
};

export const monthlyRevenueDownloadConfig = (
  table: string,
  dataMap: Record<string, any[]>
): DownloadConfig | null => {
  if (table === "Zusammenfassung") return null;

  return {
    data: dataMap.estimatedMonthlyRevenue,
    columns: plotDataTableConfigs.estimatedMonthlyRevenue.columns,
    title: "Geschätzter monatlicher Umsatz"
  };
};

export const therapistStatsDownloadConfig = (
  dataMap: Record<string, any[]>,
  startDate: Date,
  endDate: Date
): DownloadConfig => {
  return {
    data: dataMap.therapistTable,
    columns: therapistTableConfigs.therapists.columns,
    title: "Therapeutenübersicht aller Standorte"
  };
};

export const rxBlankoStatsDownloadConfig = (
  plot: string,
  table: string,
  dataMap: Record<string, any[]>
): DownloadConfig | null => {
  if (table === "Zusammenfassung") return null;

  switch (plot) {
    case "all rx blanko count":
      return {
        data: dataMap.allRxBlankoCount,
        columns: plotDataTableConfigs.totalRxBlankoCount.columns,
        title: "Anzahl der Verordnungen (Blanko)"
      };
    case "all rx blanko value":
      return {
        data: dataMap.allRxBlankoValue,
        columns: plotDataTableConfigs.totalRxBlankoValue.columns,
        title: "Gesamtwert der Verordnungen (Blanko)"
      };
    case "rx blanko therapist count":
      return {
        data: dataMap.rxBlankoTherapistCount,
        columns: plotDataTableConfigs.rxBlankoTherapistCount.columns,
        title: "Anzahl der Therapeuten pro Rezept (Blanko)"
      };
    case "rx blanko lifespan distribution":
      return {
        data: dataMap.rxBlankoLifespan,
        columns: plotDataTableConfigs.rxBlankoLifespan.columns,
        title: "Verteilung der Bearbeitungs dauer der Rezepten (Blanko)"
      }
    default:
      throw new Error(`Unsupported rx blanko plot type: ${plot}`);
  }
};

export const yearEndRxUnfinishedValuesDownloadConfig = (
  dataMap: Record<string, any[]>,
): DownloadConfig => {
  return {
    data: dataMap.yearEndRxUnfinishedValues,
    columns: yearEndRxUnfinishedValuesTableConfigs.yearEndRxUnfinishedValues.columns,
    title: "Jahresendwerte nicht abgerechneter Verordnungen"
  };
};